import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UiTagComponent } from '../../../../../shared/components/ui-tag/ui-tag.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { VendaTipoDocumentoEnum } from '../../../../../shared/enums/vendas/venda-tipo-documento.enum';
import { VendaTipoIntegracaoEnum, VendaTipoIntegracaoIndicador } from '@shared/enums/vendas/venda-tipo-integracao.enum';
import { VendaDocumentoStruct } from '../../../../../shared/services/structs/vendas/venda-documento.struct';
import {
    acoesOportunidade,
    formatarNumeroDocumento,
    PvAcoesOportunidadeVm,
    PvPermissoesOportunidade,
    temMarcaOportunidade,
    TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR,
    tooltipMarcaOportunidade,
} from '../../processo-venda.models';

/**
 * Card do board. Só apresentação: quem decide o que é arrastável e o que acontece no clique
 * é a coluna, que conhece as regras de movimentação.
 *
 * O menu de ações lista os quatro itens do protótipo desde já; cada um acende quando a
 * mecânica correspondente existir. Mostrar o item desabilitado, em vez de escondê-lo, mantém a
 * mesma posição no menu quando a ação chegar.
 */
@Component({
    selector: 'app-pv-card',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatMenuModule, MatDividerModule, MatTooltipModule, UiTagComponent, AbntPipe],
    templateUrl: './pv-card.component.html',
    styleUrls: ['./pv-card.component.css'],
})
export class PvCardComponent {
    @Input({ required: true }) card: VendaDocumentoStruct;

    /** Card em faixa de rotina externa: exibe progresso e não abre o documento. */
    @Input() isFaixaExterna = false;

    /**
     * Habilita o "Criar modelo" do menu. Quem decide é a coluna, que sabe se a etapa é de
     * fluxo principal — o card sozinho só conhece o próprio tipo de documento.
     */
    @Input() podeCriarModelo = false;

    /**
     * Habilita o "Excluir". A trava de integração é do SERVIDOR: o card não sabe se o documento
     * já gerou nota ou lançamento, e fingir que sabe daria uma recusa escrita em dois lugares.
     * Aqui só a permissão do usuário.
     */
    @Input() podeExcluir = false;

    /**
     * Habilita o "Clonar" (task 148656): a permissão de inserir. Clonar não grava — abre a tela
     * do documento novo com o conteúdo do card, e quem salva é o usuário.
     */
    @Input() podeClonar = false;

    /** Permissões das ações de oportunidade (RX-04, RX-05), resolvidas uma vez pelo shell. */
    @Input({ required: true }) permissoesOportunidade: PvPermissoesOportunidade;

    /** "Lançar financeiro": a permissão de inserir (a do pedido). Só aparece no pedido sem financeiro lançado. */
    @Input() podeLancarFinanceiro = false;

    /** "Excluir financeiro": Vendas_ProcessoVenda_ExcluirFinanceiro. Só aparece no pedido com financeiro lançado. */
    @Input() podeExcluirFinanceiro = false;

    /** "Lançar estoque": a permissão de inserir (a do pedido). Só aparece no pedido sem estoque lançado. */
    @Input() podeLancarEstoque = false;

    /** "Excluir estoque": Vendas_ProcessoVenda_ExcluirEstoque. Só aparece no pedido com estoque lançado. */
    @Input() podeExcluirEstoque = false;

    /** "Editar registro" do menu: abre o documento, igual ao clique no card. */
    @Output() editar = new EventEmitter<VendaDocumentoStruct>();
    @Output() criarModelo = new EventEmitter<VendaDocumentoStruct>();
    @Output() clonar = new EventEmitter<VendaDocumentoStruct>();
    @Output() verHistorico = new EventEmitter<VendaDocumentoStruct>();
    @Output() verOportunidadesCliente = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluir = new EventEmitter<VendaDocumentoStruct>();
    @Output() verOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() criarOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() vincularOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() desvincularOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() lancarFinanceiro = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluirFinanceiro = new EventEmitter<VendaDocumentoStruct>();
    @Output() lancarEstoque = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluirEstoque = new EventEmitter<VendaDocumentoStruct>();
    /** Clique no indicador F: os lançamentos do financeiro, só para consultar. */
    @Output() verFinanceiro = new EventEmitter<VendaDocumentoStruct>();

    public readonly vendaTipoDocumentoEnum = VendaTipoDocumentoEnum;
    public readonly tooltipOportunidadeOutroVendedor = TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR;

    /** Marca discreta "#N" do documento vinculado (RX-07); sem vínculo ou sem Visualizar do CRM, nada. */
    public get temMarcaOportunidade(): boolean {
        return temMarcaOportunidade(this.card);
    }

    public get tooltipMarcaOportunidade(): string {
        return tooltipMarcaOportunidade(this.card);
    }

    /** Itens de oportunidade do menu (D3): rótulo e motivo de bloqueio de cada um. */
    public get acoesOportunidade(): PvAcoesOportunidadeVm {
        return acoesOportunidade(this.card, this.card.idPessoaCliente, this.permissoesOportunidade);
    }

    /** Só o pedido lança financeiro; o orçamento nunca gera contas (DF5). */
    public get ehPedido(): boolean {
        return this.card.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda;
    }

    /** Indicadores das integrações lançadas, na ordem fixa (E, F) — a mesma função da lista. */
    public get integracoes(): { sigla: string; rotulo: string; cor: string; isFinanceiro: boolean }[] {
        const indicadores: { sigla: string; rotulo: string; cor: string; isFinanceiro: boolean }[] = [];
        if (this.card.isEstoqueLancado) {
            const estoque = VendaTipoIntegracaoIndicador[VendaTipoIntegracaoEnum.Estoque];
            indicadores.push({ sigla: estoque.sigla, rotulo: 'Estoque lançado', cor: estoque.cor, isFinanceiro: false });
        }
        if (this.card.isFinanceiroLancado) {
            const financeiro = VendaTipoIntegracaoIndicador[VendaTipoIntegracaoEnum.Financeiro];
            indicadores.push({ sigla: financeiro.sigla, rotulo: 'Financeiro lançado. Clique para ver os lançamentos.', cor: financeiro.cor, isFinanceiro: true });
        }
        return indicadores;
    }

    /**
     * Resumo textual dos itens. Sem o nome do produto ainda: resolvê-lo exigiria um client
     * para a API de Produto, e a contagem já cumpre o papel até o cadastro do documento existir.
     */
    public get resumoItens(): string {
        if (!this.card.qtdItens) return 'Sem itens';

        return this.card.qtdItens === 1 ? '1 item' : `${this.card.qtdItens} itens`;
    }

    /** Iniciais do responsável para o avatar. Sem nome resolvido, cai num traço. */
    public get iniciaisVendedor(): string {
        const nome = (this.card.nomeVendedor ?? '').trim();
        if (!nome) return '—';

        const partes = nome.split(/\s+/).filter(p => p.length > 0);
        const primeira = partes[0]?.charAt(0) ?? '';
        const ultima = partes.length > 1 ? (partes[partes.length - 1]?.charAt(0) ?? '') : '';

        return (primeira + ultima).toUpperCase();
    }

    /**
     * Cor do avatar derivada do id do vendedor — determinística, para o mesmo responsável ter
     * sempre a mesma cor sem precisar de campo no banco.
     */
    public get corAvatar(): string {
        const paleta = ['#2563EB', '#1E8E3E', '#7C3AED', '#EF4823', '#0E8F8F', '#C79000'];
        return paleta[this.card.idVendedor % paleta.length];
    }

    /**
     * Data do rodapé. No Orçamento é a data do próximo contato, quando informada; no Pedido é
     * sempre a data do registro.
     */
    public get ehModelo(): boolean {
        return this.card.idVendaTipoDocumento === VendaTipoDocumentoEnum.Modelo;
    }

    /** "#M-02" no modelo, número puro no resto. Prefixo é apresentação; o banco guarda int. */
    public get numeroExibido(): string {
        return formatarNumeroDocumento(this.card.numero, this.card.idVendaTipoDocumento);
    }

    /** Orçamento mostra o próximo contato (ou nada, quando não há); pedido mostra a data do registro. */
    public get dataRodape(): string | null {
        const ehOrcamento = this.card.idVendaTipoDocumento === VendaTipoDocumentoEnum.Orcamento;

        return ehOrcamento ? this.card.dataProximoContato ?? null : this.card.dataEmissao;
    }

    public get tituloDataRodape(): string {
        if (this.ehModelo) return 'Modelos não têm data';

        const ehOrcamento = this.card.idVendaTipoDocumento === VendaTipoDocumentoEnum.Orcamento;
        if (!ehOrcamento) return 'Data do registro';

        return this.card.dataProximoContato ? 'Próximo contato' : 'Sem próximo contato';
    }

    /** Rótulo do progresso reportado pela rotina externa. */
    public get rotuloProgresso(): string {
        return this.card.numeroExpedicao != null ? 'Expedido' : 'Separado';
    }
}
